import type { CSSProperties, ReactNode } from 'react';
import './scan-art.css';

/** Paper stays warm off-white in both themes, as in CapturePoint's export scenes. */
const PAPER = ['#F1EDE5', '#E8E3D9', '#E2DCD1'] as const;

export interface PaperProps {
  x: number;
  y: number;
  r?: number;
  w?: number;
  h?: number;
  tone?: 0 | 1 | 2;
  /** A field lit in the accent: the document as Paige reads it. */
  mark?: boolean;
  lines?: boolean;
  className?: string;
}

/** One sheet of paper with its printed lines; the inner group is what a loop moves. */
export function Paper({
  x,
  y,
  r = 0,
  w = 40,
  h = 52,
  tone = 0,
  mark,
  lines = true,
  className,
}: PaperProps): ReactNode {
  const u = w / 40;
  return (
    <g transform={`translate(${x} ${y}) rotate(${r})`}>
      <g className={className}>
        <rect width={w} height={h} rx={4 * u} fill={PAPER[tone]} className="sa-paper-edge" />
        {lines && (
          <path
            d={`M${7 * u} ${11 * u}h${22 * u}M${7 * u} ${18 * u}h${26 * u}M${7 * u} ${25 * u}h${18 * u}M${7 * u} ${32 * u}h${23 * u}`}
            stroke="#BDB5A6"
            strokeWidth={2 * u}
            strokeLinecap="round"
          />
        )}
        {mark && (
          <rect
            x={7 * u}
            y={39 * u}
            width={16 * u}
            height={6 * u}
            rx={1.5}
            className="sa-accent"
            opacity="0.7"
          />
        )}
      </g>
    </g>
  );
}

/** A sheet-fed document scanner, the kind on a reception desk: chute, body, light, output tray. */
export function Scanner({ x = 0, y = 0 }: { x?: number; y?: number }): ReactNode {
  return (
    <g transform={`translate(${x} ${y})`}>
      <path className="sa-shade" d="M12 52 L24 6 H80 L90 52 Z" strokeLinejoin="round" />
      <Paper x={30} y={12} w={32} h={40} tone={1} className="sa-float" />
      <rect className="sa-body" x="0" y="48" width="100" height="40" rx="10" />
      <path className="sa-ink" d="M16 72h56" />
      <circle className="sa-ok" cx="86" cy="60" r="3.5" />
      <path className="sa-shade" d="M8 88 L-4 104 H104 L92 88 Z" strokeLinejoin="round" />
    </g>
  );
}

/** A desktop monitor on its stand; the screen's contents are the caller's. */
export function Monitor({
  x = 0,
  y = 0,
  children,
}: {
  x?: number;
  y?: number;
  children?: ReactNode;
}): ReactNode {
  return (
    <g transform={`translate(${x} ${y})`}>
      <path className="sa-shade" d="M54 88 L50 108 H78 L74 88 Z" strokeLinejoin="round" />
      <rect className="sa-shade" x="38" y="106" width="52" height="7" rx="3.5" />
      <rect className="sa-body" x="0" y="0" width="128" height="90" rx="9" />
      <rect className="sa-screen" x="8" y="8" width="112" height="74" rx="4" />
      {children}
    </g>
  );
}

/** A green check in a disc: it arrived, it is connected. */
export function Check({ cx, cy, r = 12 }: { cx: number; cy: number; r?: number }): ReactNode {
  const s = r / 12;
  return (
    <g>
      <circle className="sa-ok" cx={cx} cy={cy} r={r} />
      <path
        className="sa-ok-mark"
        d={`M${cx - 5 * s} ${cy + 0.5 * s}l${4 * s} ${4 * s} ${7 * s}-${8 * s}`}
      />
    </g>
  );
}

/** The picture's own width, capped by its container (scan-art.css). */
export function artStyle(width: number): CSSProperties {
  return { '--scan-art-width': `${width}px` } as CSSProperties;
}
